import React, { useState, useMemo, useEffect } from 'react'
import moment from 'moment'
import {
  CartesianGrid,
  ComposedChart,
  Area,
  Line,
  Bar,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { curveBumpX } from 'd3-shape'
import {
  Header,
  Loadable,
  NavFilter,
  Pagination,
  SearchSubmit,
  TableHead,
  useApiGet,
  useSortableTable,
  formatFilterRecords,
  navigateTo,
  formatStat,
  formatShortStat,
  isBlank,
  varClass,
  DatePicker,
  useDatePickerState,
} from '../shared'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'advertisers', label: 'Advertisers' },
  { key: 'opportunities', label: 'Opportunities' },
]

export const EpcRoutingDashboard = (props) => {
  const [tab, setTab] = useState('overview')
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<any>(null)

  if (selectedAdvertiser) {
    return (
      <div className="epc-routing">
        <DatePicker className="in-header" />
        <AdvertiserDetail
          advertiser={selectedAdvertiser}
          onBack={() => setSelectedAdvertiser(null)}
        />
      </div>
    )
  }

  return (
    <div className="epc-routing">
      <Header label="EPC Routing" />

      <p className="text-light m-b-5">
        EPC Routing automatically tests affiliate trackers against each other and routes traffic to the one that earns most per click — regardless of commission rate.
      </p>

      {tab !== 'networks' && <DatePicker className="in-header" />}

      <div className="epc-tabs m-b-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={varClass({
              'epc-tab': true,
              'active': tab === t.key,
            })}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'advertisers' && <AdvertisersTab onSelect={setSelectedAdvertiser} />}
      {tab === 'opportunities' && <OpportunitiesTab />}
    </div>
  )
}

const OverviewTab = () => {
  const datePickerState = useDatePickerState()
  const fromDate = datePickerState?.range?.startDate
  const toDate = datePickerState?.range?.endDate

  const data = useApiGet<any>(
    '/epc/v1/dashboard/overview',
    { fromDate, toDate },
    [fromDate, toDate]
  )
  const kpis = data?.kpis
  const pipeline = data?.pipeline
  const periods = data?.periods
  const timeSeries = data?.timeSeries || []

  const impactPeriod = periods
    ? `${moment(periods.impactFrom).format('MMM D')} – ${moment(periods.impactTo).format('MMM D')}`
    : undefined
  const snapshotPeriod = periods
    ? `as of ${moment(periods.snapshotDate).format('MMM D')}`
    : undefined

  return (
    <Loadable data={data}>
      <div className="epc-kpis">
        <KpiCard
          title="Total Extra Revenue"
          suffix="DKK"
          value={kpis?.totalValidatedImpact}
          color="green"
          sub="Cumulative extra revenue earned by routing to higher-performing trackers instead of the highest-commission tracker"
          period={impactPeriod}
        />
        <KpiCard
          title="Avg. Extra Revenue / Click"
          suffix="DKK"
          value={kpis?.validatedLiftPerClick}
          decimals={4}
          sub="Average extra DKK per click, measured on days with conversions in groups where traffic was actively split between trackers"
          period={impactPeriod}
        />
        <DivergenceCard kpis={kpis} pipeline={pipeline} period={snapshotPeriod} />
        <KpiCard
          title="EPC Coverage"
          value={kpis?.epcManagedClickRate != null ? kpis.epcManagedClickRate * 100 : undefined}
          decimals={1}
          suffix="%"
          sub="of monetized clicks are in groups where EPC routing is active (excluding Amazon, Heylink Inbound & Discover clicks)"
          period={snapshotPeriod}
        />
      </div>

      <div className="m-t-20">
        <GroupPipeline pipeline={pipeline} period={snapshotPeriod} />
      </div>

      <div className="epc-charts-dual m-t-20">
        <DailyLiftChart timeSeries={timeSeries} />
        <PipelineTimeChart timeSeries={timeSeries} />
      </div>

    </Loadable>
  )
}

// ---------------------------------------------------------------------------
// Opportunities Tab
// ---------------------------------------------------------------------------


const OpportunitiesTab = () => {
  const datePickerState = useDatePickerState()
  const fromDate = datePickerState?.range?.startDate
  const toDate = datePickerState?.range?.endDate

  const channels = useApiGet<any>('/channels/v1/list')
  const networks = useApiGet<any>('/networks/v1/list')

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState([
    { key: 'channelUuid', label: '', value: '' },
    { key: 'priorityNetworkUuid', label: '', value: '' },
  ])
  const [excludeNetworks, setExcludeNetworks] = useState<any[]>([])
  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'totalClicks',
    dir: 'desc',
  })

  const channelUuid = filters.find((f) => f.key === 'channelUuid')?.value || ''
  const priorityNetworkUuid = filters.find((f) => f.key === 'priorityNetworkUuid')?.value || ''
  const offset = (page - 1) * perPage
  const excludeUuids = excludeNetworks.map((n) => n.networkUuid).join(',')

  const params: any = { fromDate, toDate, sort, direction: dir, offset, limit: perPage }
  if (search) params.search = search
  if (channelUuid) params.channelUuid = channelUuid
  if (priorityNetworkUuid) params.priorityNetworkUuid = priorityNetworkUuid
  if (excludeUuids) params.excludeNetworkUuids = excludeUuids

  const response = useApiGet<any>(
    '/epc/v1/dashboard/opportunities',
    params,
    [fromDate, toDate, sort, dir, offset, search, channelUuid, priorityNetworkUuid, excludeUuids]
  )

  const opportunities = response?.opportunities || []
  const total = response?.total || 0
  const maxPage = Math.ceil(total / perPage)

  useEffect(() => {
    setPage(1)
  }, [search, sort, dir, fromDate, toDate, channelUuid, priorityNetworkUuid, excludeUuids])

  const headProps = { sort, dir, toggleSort }

  const toggleExclude = (network) => {
    setExcludeNetworks((prev) => {
      const exists = prev.some((n) => n.networkUuid === network.networkUuid)
      return exists
        ? prev.filter((n) => n.networkUuid !== network.networkUuid)
        : [...prev, network]
    })
  }

  // Collect unique challenger networks from results for the exclude filter
  const challengerNetworks = useMemo(() => {
    const map = new Map()
    for (const opp of opportunities) {
      for (const c of opp.challengers || []) {
        if (!map.has(c.networkUuid)) map.set(c.networkUuid, c)
      }
    }
    return Array.from(map.values()).sort((a, b) => a.networkName.localeCompare(b.networkName))
  }, [opportunities])

  return (
    <>
      <div className="card">
        <div className="p-y-20 m-t--3 m-b-10">
          <div className="row row-space-between p-t-05 m-t-4 m-b-3">
            <SearchSubmit
              value={search}
              setValue={setSearch}
              placeholder="Search advertiser"
              className="search w-40"
              inputClassName="w-100 p-r-8"
              highlight
            />
            <div className="text-light text-small p-r-6">
              {total} opportunit{total !== 1 ? 'ies' : 'y'}
            </div>
          </div>

          {!isBlank(channels) && (
            <div className="m-t-2 m-b-4">
              <NavFilter
                filters={filters}
                setFilters={setFilters}
                filterLabels={{
                  channelUuid: 'Site',
                  priorityNetworkUuid: 'Locked to',
                }}
                filterOptions={{
                  channelUuid: {
                    '': 'All sites',
                    ...formatFilterRecords(channels, 'channelUuid', 'channelName', 'channelDomain'),
                  },
                  priorityNetworkUuid: {
                    '': 'All networks',
                    ...formatFilterRecords(networks, 'networkUuid', 'networkName'),
                  },
                }}
                onRemove={(index, key) => {
                  const next = [...filters]
                  next[index].label = ''
                  next[index].value = ''
                  setFilters(next)
                }}
                hideChecks
                hideAdd
                hideRemove
              />
            </div>
          )}

          {challengerNetworks.length > 0 && (
            <div className="epc-exclude-section">
              <span className="text-light text-small m-r-2">Exclude challengers:</span>
              <div className="epc-exclude-tags">
                {challengerNetworks.map((n) => {
                  const isExcluded = excludeNetworks.some((e) => e.networkUuid === n.networkUuid)
                  return (
                    <button
                      key={n.networkUuid}
                      className={varClass({
                        'epc-exclude-tag': true,
                        'active': isExcluded,
                      })}
                      onClick={() => toggleExclude(n)}
                    >
                      {n.networkName}
                      {isExcluded && ' ✕'}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <Loadable data={response}>
          <div className="table-container-overflow m-x--6">
            <table className="table table-bordered table-sortable table-hoverable">
              <thead className="text-nowrap">
                <tr>
                  <TableHead value="advertiserDomain" label="Advertiser" className="w-20" {...headProps} />
                  <th>Channel</th>
                  <th>Country</th>
                  <th>Locked To</th>
                  <th>Challengers</th>
                  <TableHead value="totalClicks" label="Clicks" desc {...headProps} />
                </tr>
              </thead>
              <tbody>
                {opportunities.map((opp, i) => (
                  <tr
                    key={i}
                    className="cursor-pointer"
                    onClick={(event) => navigateTo(event, `/merchants/${opp.advertiserUuid}`)}
                  >
                    <td>
                      <span className="text-dark text-bold">{opp.advertiserDomain}</span>
                    </td>
                    <td className="text-small">{opp.channelName}</td>
                    <td>{opp.countryCode}</td>
                    <td>
                      <div className="epc-network-tags">
                        {(opp.priorityNetworks || []).map((n) => (
                          <span key={n.networkUuid} className="epc-mode-badge" style={{ color: '#6a98fd', background: '#6a98fd1A' }}>
                            {n.networkName}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="epc-network-tags">
                        {(opp.challengers || []).map((n) => (
                          <span key={n.networkUuid} className="epc-mode-badge" style={{ color: '#828B9E', background: '#828B9E1A' }}>
                            {n.networkName}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-right">{formatStat(opp.totalClicks || 0, 'integer')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {maxPage > 1 && (
            <div className="m-t-4 m-b-2">
              <Pagination page={page} setPage={setPage} maxPage={maxPage} />
            </div>
          )}
        </Loadable>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Advertisers Tab
// ---------------------------------------------------------------------------

const perPage = 50

const AdvertisersTab = (props) => {
  const { onSelect } = props
  const datePickerState = useDatePickerState()
  const fromDate = datePickerState?.range?.startDate
  const toDate = datePickerState?.range?.endDate

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'realizedRevenue',
    dir: 'desc',
  })

  const offset = (page - 1) * perPage

  const params: any = { fromDate, toDate, sort, direction: dir, offset, limit: perPage }
  if (search) params.search = search

  const response = useApiGet<any>(
    '/epc/v1/dashboard/advertisers',
    params,
    [fromDate, toDate, sort, dir, offset, search]
  )

  const advertisers = response?.advertisers || []
  const total = response?.total || 0
  const maxPage = Math.ceil(total / perPage)

  useEffect(() => {
    setPage(1)
  }, [search, sort, dir, fromDate, toDate])

  const headProps = { sort, dir, toggleSort }

  return (
    <>
      <div className="card">
        <div className="p-y-20 m-t--3 m-b-10">
          <div className="row row-space-between p-t-05 m-t-4 m-b-3">
            <SearchSubmit
              value={search}
              setValue={setSearch}
              placeholder="Search advertiser"
              className="search w-40"
              inputClassName="w-100 p-r-8"
              highlight
            />
            <div className="text-light text-small p-r-6">
              {total} advertiser{total !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <Loadable data={response}>
          <div className="table-container-overflow m-x--6">
            <table className="table table-bordered table-sortable table-hoverable">
              <thead className="text-nowrap">
                <tr>
                  <TableHead value="advertiserDomain" label="Domain" className="w-20" {...headProps} />
                  <TableHead value="groupCount" label="Groups" desc {...headProps} />
                  <TableHead value="routingStatus" label="Routing" {...headProps} />
                  <TableHead value="championEpcAvg" label="EPC vs Commission" desc {...headProps} />
                  <TableHead value="totalClicks" label="Clicks" desc {...headProps} />
                  <TableHead value="realizedRevenue" label="Revenue" desc {...headProps} />
                  <TableHead value="validatedImpact" label="Impact" desc {...headProps} />
                  <TableHead value="liftPerClick" label="Lift/Click" desc {...headProps} />
                </tr>
              </thead>
              <tbody>
                {advertisers.map((adv) => (
                  <AdvTableRow key={adv.advertiserUuid} adv={adv} onSelect={onSelect} />
                ))}
              </tbody>
            </table>
          </div>

          {maxPage > 1 && (
            <div className="m-t-4 m-b-2">
              <Pagination page={page} setPage={setPage} maxPage={maxPage} />
            </div>
          )}
        </Loadable>
      </div>
    </>
  )
}

const AdvTableRow = (props) => {
  const { adv, onSelect } = props
  const isOptimal = adv.routingStatus === 'OPTIMAL'
  const epcDiff = (adv.championEpcAvg || 0) - (adv.commissionWinnerEpcAvg || 0)

  return (
    <tr className="cursor-pointer" onClick={() => onSelect(adv)}>
      <td>
        <span className="text-dark text-bold">{adv.advertiserDomain}</span>
      </td>
      <td className="text-right">{adv.groupCount}</td>
      <td>
        <RoutingStatusBadge status={adv.routingStatus} />
      </td>
      <td className="text-right">
        {isOptimal ? (
          <span className="text-light">Already optimal</span>
        ) : (
          <>
            <span className={epcDiff > 0 ? 'text-bold text-green' : 'text-bold'}>{(adv.championEpcAvg || 0).toFixed(2)}</span>
            <span className="text-light m-l-1">vs</span>
            <span className="text-light m-l-1">{(adv.commissionWinnerEpcAvg || 0).toFixed(2)}</span>
            {epcDiff > 0 && (
              <span className="text-green text-smaller m-l-1">+{epcDiff.toFixed(2)}</span>
            )}
          </>
        )}
      </td>
      <td className="text-right">{formatStat(adv.totalClicks, 'integer')}</td>
      <td className="text-right">{(adv.realizedRevenue || 0).toFixed(2)}</td>
      <td className="text-right">
        <span className={adv.validatedImpact > 0 ? 'text-green' : ''}>
          {(adv.validatedImpact || 0).toFixed(2)}
        </span>
      </td>
      <td className="text-right">{(adv.liftPerClick || 0).toFixed(4)}</td>
    </tr>
  )
}

const RoutingStatusBadge = (props) => {
  const { status } = props
  const color = ROUTING_COLORS[status] || '#828B9E'
  return (
    <span
      className="epc-mode-badge"
      style={{ color, background: `${color}1A` }}
    >
      {ROUTING_LABELS[status] || status || '–'}
    </span>
  )
}


const DivergenceCard = (props) => {
  const { kpis, pipeline, period } = props
  const activelyRouted = (pipeline?.exploring?.total || 0) + (pipeline?.confident || 0)
  const classified = (kpis?.divergedGroups || 0) + (kpis?.confirmedGroups || 0)
  const unclassified = Math.max(0, activelyRouted - classified)

  return (
    <div className="card card-nooverflow p-x-5 p-b-4 p-t-4">
      <div className="row row-space-between m-b-2">
        <div className="text-light text-bold text-small">Routing Decisions</div>
        {period && <div className="text-light text-smaller o-50">{period}</div>}
      </div>
      <div className="text-larger text-boldest text-dark m-b-1">
        {kpis?.divergedGroups ?? '–'}
        {kpis?.divergedGroupRate != null && (
          <span
            className="text-small text-light m-l-1"
            title="Of groups currently routed by EPC (Exploring or Confident), the fraction picking a tracker different from the commission-rank winner. Cold Start groups are excluded from this rate."
          >
            ({(kpis.divergedGroupRate * 100).toFixed(1)}%)
          </span>
        )}
      </div>
      <div className="text-light text-small m-b-3">
        Of advertiser × channel × country groups currently routed by EPC (Exploring or Confident), how many pick a different tracker than commission ranking alone would. Cold Start groups are shown separately under "still gathering data".
      </div>
      <div className="epc-divergence-rows">
        <div className="epc-divergence-row">
          <span className="epc-divergence-dot" style={{ background: '#EBB617' }} />
          <span className="text-bold text-small">{kpis?.divergedGroups ?? '–'}</span>
          <span className="text-light text-small m-l-1">overriding — routing to tracker with higher measured performance than commission winner</span>
        </div>
        <div className="epc-divergence-row">
          <span className="epc-divergence-dot" style={{ background: '#3fac73' }} />
          <span className="text-bold text-small">{kpis?.confirmedGroups ?? '–'}</span>
          <span className="text-light text-small m-l-1">confirmed — evaluated alternatives and agreed the commission winner is also best</span>
        </div>
        <div className="epc-divergence-row">
          <span className="epc-divergence-dot" style={{ background: '#828B9E' }} />
          <span className="text-bold text-small">{kpis?.noChampionGroups ?? '–'}</span>
          <span className="text-light text-small m-l-1">still gathering data — Cold Start groups without enough clicks yet to pick a champion</span>
        </div>
      </div>
      {unclassified > 0 && (
        <div className="text-light text-smaller m-t-3 o-70">
          {unclassified} {unclassified === 1 ? 'group' : 'groups'} in Exploring/Confident had no activity on the snapshot day and aren't classified above.
        </div>
      )}
    </div>
  )
}

const KpiCard = (props) => {
  const { title, value, suffix, decimals, integer, sub, color, valueSuffix, secondary, period } = props

  const formatted = useMemo(() => {
    if (value == null) return '–'
    if (integer) return formatStat(value, 'integer')
    if (decimals === 4) return (value || 0).toFixed(4)
    if (decimals === 1) return (value || 0).toFixed(1)
    return (value || 0).toFixed(2)
  }, [value, integer, decimals])

  return (
    <div className="card card-nooverflow p-x-5 p-b-4 p-t-4">
      <div className="row row-space-between m-b-2">
        <div className="text-light text-bold text-small">
          {title}{' '}
          {suffix && <span className="o-50">{suffix}</span>}
        </div>
        {period && <div className="text-light text-smaller o-50">{period}</div>}
      </div>
      <div
        className={varClass({
          'text-larger text-boldest': true,
          'text-green': color === 'green' && value > 0,
          'text-dark': color !== 'green' || !value || value <= 0,
        })}
      >
        {formatted}
        {valueSuffix && <span className="text-small text-light">{valueSuffix}</span>}
      </div>
      {sub && (
        <div className="text-light text-small m-t-1">{sub}</div>
      )}
      {secondary && (
        <div className="text-dark text-small text-bold m-t-1">{secondary}</div>
      )}
    </div>
  )
}

const LIFT_COLOR_POSITIVE = '#3fac7366'
const LIFT_COLOR_LEARNING = '#EBB61766'
const LIFT_COLOR_ZERO = '#828B9E66'

const liftBarColor = (impact: number): string => {
  if (impact > 0) return LIFT_COLOR_POSITIVE
  if (impact < 0) return LIFT_COLOR_LEARNING
  return LIFT_COLOR_ZERO
}

const DailyLiftChart = (props) => {
  const { timeSeries } = props

  const chartData = useMemo(() => {
    if (!timeSeries?.length) return []
    return timeSeries.map((row) => ({
      date: row.calculationDate,
      directImpact: row.directImpact,
      dailyClicks: row.dailyClicks,
      barColor: liftBarColor(row.directImpact || 0),
    }))
  }, [timeSeries])

  if (!chartData.length) return null

  return (
    <div className="card flex-1">
      <div className="card-body p-t-1">
        <div className="m-b-4">
          <div className="text-dark text-bolder">Daily Measured Lift</div>
          <div className="text-light text-smaller m-t-1">Zero bars are expected — they occur on days with no conversions and do not indicate missing data</div>
        </div>
        <ResponsiveContainer width="99.9%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 16, left: 0, right: 0, bottom: 0 }}>
            <CartesianGrid horizontal vertical={false} strokeDasharray="6" stroke="#EDEFF2" />
            <Tooltip content={<LiftChartTooltip />} />
            <Bar
              dataKey="directImpact"
              yAxisId="impact-y"
              barSize={14}
              radius={2}
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.barColor} />
              ))}
            </Bar>
            <Line
              dataKey="dailyClicks"
              yAxisId="clicks-y"
              type={curveBumpX}
              stroke="#6a98fd"
              strokeWidth={2}
              dot={false}
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tick={<ChartXTick />}
            />
            <YAxis
              yAxisId="impact-y"
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              interval="preserveStartEnd"
              orientation="left"
              tick={<ChartYTick orientation="left" />}
            />
            <YAxis
              yAxisId="clicks-y"
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              interval="preserveStartEnd"
              orientation="right"
              tick={<ChartYTick orientation="right" />}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="row row-center m-t-3" style={{ gap: 16 }}>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#3fac73' }} />
            Positive lift
          </span>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#EBB617' }} />
            Learning cost
          </span>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#828B9E' }} />
            No conversions
          </span>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#6a98fd' }} />
            Daily Clicks
          </span>
        </div>
        <div className="text-light text-smaller m-t-2">
          Amber bars are days where commission's pick out-earned EPC's pick — the cost of exploration. Single-day variance is expected; the Total Extra Revenue KPI above is the running cumulative.
        </div>
      </div>
    </div>
  )
}

const PipelineTimeChart = (props) => {
  const { timeSeries } = props

  const chartData = useMemo(() => {
    if (!timeSeries?.length) return []
    return timeSeries.map((row) => ({
      date: row.calculationDate,
      coldStart: row.coldStartGroups,
      exploring: row.exploringGroups,
      confident: row.confidentGroups,
    }))
  }, [timeSeries])

  if (!chartData.length) return null

  return (
    <div className="card flex-1">
      <div className="card-body p-t-1">
        <div className="m-b-4">
          <div className="text-dark text-bolder">Active Groups Over Time</div>
          <div className="text-light text-smaller m-t-1">Groups with click activity each day, by mode. The Group Pipeline above shows the full state-machine snapshot including idle groups.</div>
        </div>
        <ResponsiveContainer width="99.9%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 16, left: 0, right: 0, bottom: 0 }}>
            <CartesianGrid horizontal vertical={false} strokeDasharray="6" stroke="#EDEFF2" />
            <Tooltip content={<PipelineChartTooltip />} />
            <Area
              dataKey="coldStart"
              yAxisId="groups-y"
              stackId="groups"
              type={curveBumpX}
              stroke="#828B9E"
              strokeWidth={0}
              fill="#828B9E66"
            />
            <Area
              dataKey="exploring"
              yAxisId="groups-y"
              stackId="groups"
              type={curveBumpX}
              stroke="#EBB617"
              strokeWidth={0}
              fill="#EBB61766"
            />
            <Area
              dataKey="confident"
              yAxisId="groups-y"
              stackId="groups"
              type={curveBumpX}
              stroke="#3fac73"
              strokeWidth={0}
              fill="#3fac7366"
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tick={<ChartXTick />}
            />
            <YAxis
              yAxisId="groups-y"
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              interval="preserveStartEnd"
              orientation="left"
              tick={<ChartYTick orientation="left" />}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="row row-center m-t-3" style={{ gap: 16 }}>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#828B9E' }} />
            Cold Start
          </span>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#EBB617' }} />
            Exploring
          </span>
          <span className="epc-chart-legend">
            <span className="epc-chart-legend-dot" style={{ background: '#3fac73' }} />
            Confident
          </span>
        </div>
      </div>
    </div>
  )
}

const ChartXTick = (props) => (
  <text x={props.x} y={props.y + 20} textAnchor="middle" fill="#828B9E">
    {moment(props.payload.value).format('MMM D')}
  </text>
)

const ChartYTick = (props) => (
  <g
    transform={
      props.orientation === 'left'
        ? `translate(${16},${props.y + 2})`
        : `translate(${-32},${props.y + 2})`
    }
  >
    <text
      x={props.orientation === 'left' ? 0 : '99%'}
      y={0}
      textAnchor="start"
      fill="#828B9E"
    >
      {formatShortStat(props.payload.value)}
    </text>
  </g>
)

const LiftChartTooltip = (props) => {
  const { payload } = props
  if (isBlank(payload) || isBlank(payload[0])) return null
  const row = payload[0].payload
  const impact = row.directImpact || 0
  const liftLabel = impact > 0 ? 'Positive lift' : impact < 0 ? 'Learning cost' : 'No conversions'
  const liftDotColor = impact > 0 ? '#3fac73' : impact < 0 ? '#EBB617' : '#828B9E'
  return (
    <div className="chart-tooltip chart-tooltip-light">
      <div className="chart-tooltip-header">{row.date}</div>
      <div className="chart-tooltip-body">
        <ul>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: liftDotColor }} />
              {liftLabel}
            </span>{' '}
            <span>{impact.toFixed(2)} DKK</span>
          </li>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#6a98fd' }} />
              Daily clicks across all EPC-managed groups
            </span>{' '}
            <span>{formatStat(row.dailyClicks, 'integer')}</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

const PipelineChartTooltip = (props) => {
  const { payload } = props
  if (isBlank(payload) || isBlank(payload[0])) return null
  const row = payload[0].payload
  return (
    <div className="chart-tooltip chart-tooltip-light">
      <div className="chart-tooltip-header">{row.date}</div>
      <div className="chart-tooltip-body">
        <ul>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#828B9E' }} />
              Cold Start
            </span>{' '}
            <span>{row.coldStart}</span>
          </li>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#EBB617' }} />
              Exploring
            </span>{' '}
            <span>{row.exploring}</span>
          </li>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#3fac73' }} />
              Confident
            </span>{' '}
            <span>{row.confident}</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

const MODE_COLORS = {
  COLD_START: '#828B9E',
  EXPLORING: '#EBB617',
  CONFIDENT: '#3fac73',
}

const MODE_LABELS = {
  COLD_START: 'Cold Start',
  EXPLORING: 'Exploring',
  CONFIDENT: 'Confident',
}

const ROUTING_COLORS = {
  OPTIMAL: '#3fac73',
  DIVERGED: '#EBB617',
  COLD: '#828B9E',
}

const ROUTING_LABELS = {
  OPTIMAL: 'Optimal',
  DIVERGED: 'Diverged',
  COLD: 'Cold',
}

const GroupPipeline = (props) => {
  const { pipeline, period } = props

  if (!pipeline) return null

  const total = (pipeline.coldStart || 0) + (pipeline.exploring?.total || 0) + (pipeline.confident || 0)

  const segments = [
    { key: 'coldStart', label: 'Cold Start', sublabel: 'gathering baseline data', count: pipeline.coldStart || 0, color: '#828B9E' },
    { key: 'exploring', label: 'Exploring', sublabel: 'actively comparing trackers', count: pipeline.exploring?.total || 0, color: '#EBB617' },
    { key: 'confident', label: 'Confident', sublabel: 'champion locked in', count: pipeline.confident || 0, color: '#3fac73' },
  ]

  return (
    <div className="card">
      <div className="card-body">
        <div className="row row-space-between m-b-2">
          <div className="text-dark text-bolder">Group Pipeline</div>
          {period && <div className="text-light text-smaller o-50">{period}</div>}
        </div>
        <div className="text-light text-small m-b-4">
          A group = one advertiser × channel × country combination. Each group moves through three stages as EPC gathers enough data to pick a winning tracker.
        </div>

        <div className="epc-mode-bar m-b-5">
          {segments.map((s) => (
            <div
              key={s.key}
              className="epc-mode-bar-segment"
              style={{
                width: total > 0 ? `${(s.count / total) * 100}%` : '33%',
                background: s.color,
              }}
            />
          ))}
        </div>

        <div className="epc-pipeline-stages">
          {segments.map((s) => (
            <div key={s.key} className="epc-pipeline-stage">
              <div className="row row-center m-b-1">
                <span className="epc-mode-dot" style={{ background: s.color }} />
                <span className="text-bold text-small">{s.label}</span>
                <span className="text-light text-small m-l-1">— {s.sublabel}</span>
              </div>
              <div className="text-dark text-larger text-boldest">{s.count}</div>
              {s.key === 'exploring' && pipeline.exploring && (
                <div className="m-t-2">
                  <div className="text-small m-b-1">
                    <span className="text-bold">{pipeline.exploring.dominant}</span>
                    <span className="text-light m-l-1">dominant champion (≥95% win prob)</span>
                  </div>
                  <div className="text-small m-b-1">
                    <span className="text-bold">{pipeline.exploring.contested}</span>
                    <span className="text-light m-l-1">contested</span>
                  </div>
                  <div className="text-small">
                    <span className="text-bold">{pipeline.exploring.divergedFromCommission}</span>
                    <span className="text-light m-l-1">routing differs from commission ranking</span>
                  </div>
                </div>
              )}
              {s.key === 'confident' && s.count === 0 && (
                <div className="text-light text-smaller m-t-2">
                  Expected from week 2 onwards
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Advertiser Detail
// ---------------------------------------------------------------------------

const AdvertiserDetail = (props) => {
  const { advertiser, onBack } = props

  const data = useApiGet<any>(
    '/epc/v1/routing/' + advertiser.advertiserUuid,
    {},
    [advertiser.advertiserUuid]
  )
  const groups = data?.groups || []

  const [selectedGroup, setSelectedGroup] = useState<any>(null)

  return (
    <>
      <div
        className="link link-dark text-bolder vertical-middle m-b-30 cursor-pointer"
        onClick={onBack}
      >
        ← Back to advertisers
      </div>

      <div className="text-dark text-bigger text-bolder m-b-6">
        {advertiser.advertiserDomain}
      </div>

      <Loadable data={data}>
        <div className="card m-b-20">
          <div className="card-body p-b-0">
            <div className="text-dark text-bolder m-b-4">Groups</div>
          </div>
          <div className="table-container-overflow m-x--6">
            <table className="table table-bordered table-hoverable">
              <thead className="text-nowrap">
                <tr>
                  <th>Channel</th>
                  <th>Country</th>
                  <th>Mode</th>
                  <th>Routing</th>
                  <th className="text-right">Daily Clicks</th>
                  <th className="text-right">Champion EPC</th>
                  <th className="text-right">Commission EPC</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => {
                  const key = `${group.channelUuid}-${group.countryCode}`
                  const isSelected = selectedGroup &&
                    selectedGroup.channelUuid === group.channelUuid &&
                    selectedGroup.countryCode === group.countryCode
                  return (
                    <tr
                      key={key}
                      className={varClass({
                        'cursor-pointer': true,
                        'active': isSelected,
                      })}
                      onClick={() => setSelectedGroup(isSelected ? null : group)}
                    >
                      <td>
                        <span className="text-dark text-bold">{group.channelName}</span>
                      </td>
                      <td>{group.countryCode}</td>
                      <td>
                        <span
                          className="epc-mode-badge"
                          style={{
                            color: MODE_COLORS[group.mode] || '#828B9E',
                            background: `${MODE_COLORS[group.mode] || '#828B9E'}1A`,
                          }}
                        >
                          {MODE_LABELS[group.mode] || group.mode}
                        </span>
                      </td>
                      <td>
                        {group.isDiverged ? (
                          <span className="epc-mode-badge" style={{ color: '#EBB617', background: '#EBB6171A' }}>Different</span>
                        ) : group.commissionWinnerTrackerUuid ? (
                          <span className="epc-mode-badge" style={{ color: '#3fac73', background: '#3fac731A' }}>Same</span>
                        ) : (
                          <span className="text-light">–</span>
                        )}
                      </td>
                      <td className="text-right">
                        {group.dailyClicks === 0 ? (
                          <span className="text-light">0</span>
                        ) : (
                          formatStat(group.dailyClicks, 'integer')
                        )}
                      </td>
                      <td className="text-right text-bold">{(group.championEpc || 0).toFixed(2)}</td>
                      <td className="text-right text-light">{(group.commissionWinnerEpc || 0).toFixed(2)}</td>
                      <td>
                        {group.mode === 'EXPLORING' ? (
                          <span className="text-small">{group.winnerConsecutiveDays || 0}/3 days</span>
                        ) : group.mode === 'CONFIDENT' ? (
                          <span className="text-small text-green">Locked</span>
                        ) : (
                          <span className="text-light">–</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {selectedGroup && (
          <>
            <GroupTrackers group={selectedGroup} />
            <GroupTimeline advertiserUuid={advertiser.advertiserUuid} group={selectedGroup} />
          </>
        )}
      </Loadable>
    </>
  )
}

const GroupTrackers = (props) => {
  const { group } = props
  const trackers = group.trackers || []
  if (!trackers.length) return null

  return (
    <div className="card m-b-20">
      <div className="card-body p-b-0">
        <div className="text-dark text-bolder m-b-2">
          Trackers — {group.channelName} · {group.countryCode}
        </div>
      </div>
      <div className="table-container-overflow m-x--6">
        <table className="table table-bordered">
          <thead className="text-nowrap">
            <tr>
              <th>Network</th>
              <th className="text-right">EPC</th>
              <th className="text-right">Weight</th>
              <th className="text-right">Win Prob</th>
              <th className="text-right">Clicks</th>
              <th className="text-right">Today</th>
              <th className="text-right">Conv</th>
              <th>Champion</th>
            </tr>
          </thead>
          <tbody>
            {trackers.map((t) => (
              <tr key={t.trackerUuid}>
                <td><span className="text-dark text-bold">{t.networkName}</span></td>
                <td className="text-right">{(t.effectiveEpc || 0).toFixed(2)}</td>
                <td className="text-right">{(t.weight * 100).toFixed(1)}%</td>
                <td className="text-right">{(t.winProbability * 100).toFixed(1)}%</td>
                <td className="text-right">{formatStat(t.clicksCount, 'integer')}</td>
                <td className="text-right">{formatStat(t.dailyClickCount, 'integer')}</td>
                <td className="text-right">{t.conversionCount}</td>
                <td>{t.isChampion ? <span className="text-green text-bold">★</span> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Group Timeline
// ---------------------------------------------------------------------------

const GroupTimeline = (props) => {
  const { advertiserUuid, group } = props
  const datePickerState = useDatePickerState()
  const fromDate = datePickerState?.range?.startDate
  const toDate = datePickerState?.range?.endDate

  const data = useApiGet<any>(
    '/epc/v1/routing/' + advertiserUuid + '/timeline',
    { channelUuid: group.channelUuid, countryCode: group.countryCode, fromDate, toDate },
    [group.channelUuid, group.countryCode, fromDate, toDate]
  )
  const timeline = data?.timeline || []

  const chartData = useMemo(() => {
    if (!timeline.length) return []
    return timeline.map((row) => ({
      date: row.calculationDate,
      dailyImpact: row.dailyImpact,
      championEpc: row.championEpc,
      counterfactualEpc: row.counterfactualEpc,
      dailyClicks: row.dailyClicks,
      measurementType: row.measurementType,
    }))
  }, [timeline])

  if (!data) return null

  return (
    <div className="card">
      <div className="card-body p-t-1">
        <div className="m-b-4">
          <div className="text-dark text-bolder">Timeline — {group.channelName} · {group.countryCode}</div>
        </div>

        <Loadable data={chartData.length ? chartData : null}>
          <ResponsiveContainer width="99.9%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 16, left: 0, right: 0, bottom: 0 }}>
              <CartesianGrid horizontal vertical={false} strokeDasharray="6" stroke="#EDEFF2" />
              <Tooltip content={<TimelineTooltip />} />
              <Bar dataKey="dailyClicks" yAxisId="clicks-y" barSize={18} fill="#828B9E1A" />
              <Bar dataKey="dailyImpact" yAxisId="impact-y" barSize={10} radius={2} fill="#3fac7366" />
              <Line dataKey="championEpc" yAxisId="epc-y" type={curveBumpX} stroke="#6a98fd" strokeWidth={2} dot={false} />
              <Line dataKey="counterfactualEpc" yAxisId="epc-y" type={curveBumpX} stroke="#EBB617" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} interval="preserveStartEnd" tick={<ChartXTick />} />
              <YAxis yAxisId="impact-y" axisLine={false} tickLine={false} interval="preserveStartEnd" orientation="left" tick={<ChartYTick orientation="left" />} />
              <YAxis yAxisId="epc-y" axisLine={false} tickLine={false} interval="preserveStartEnd" orientation="right" tick={<ChartYTick orientation="right" />} />
              <YAxis yAxisId="clicks-y" hide />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="row row-center m-t-3" style={{ gap: 16 }}>
            <span className="epc-chart-legend">
              <span className="epc-chart-legend-dot" style={{ background: '#3fac73' }} />
              Daily Impact (DKK)
            </span>
            <span className="epc-chart-legend">
              <span className="epc-chart-legend-dot" style={{ background: '#6a98fd' }} />
              Champion EPC
            </span>
            <span className="epc-chart-legend">
              <span className="epc-chart-legend-dot" style={{ background: '#EBB617' }} />
              Commission EPC
            </span>
          </div>
        </Loadable>
      </div>
    </div>
  )
}

const TimelineTooltip = (props) => {
  const { payload } = props
  if (isBlank(payload) || isBlank(payload[0])) return null
  const row = payload[0].payload
  return (
    <div className="chart-tooltip chart-tooltip-light">
      <div className="chart-tooltip-header">{row.date}</div>
      <div className="chart-tooltip-body">
        <ul>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#3fac73' }} />
              Daily Impact
            </span>{' '}
            <span>{(row.dailyImpact || 0).toFixed(2)} DKK</span>
          </li>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#6a98fd' }} />
              Champion EPC
            </span>{' '}
            <span>{(row.championEpc || 0).toFixed(4)}</span>
          </li>
          <li className="chart-tooltip">
            <span>
              <span className="chart-tooltip-dot" style={{ background: '#EBB617' }} />
              Commission EPC
            </span>{' '}
            <span>{(row.counterfactualEpc || 0).toFixed(4)}</span>
          </li>
          <li className="chart-tooltip">
            <span>Daily Clicks</span>{' '}
            <span>{formatStat(row.dailyClicks, 'integer')}</span>
          </li>
          {row.measurementType && (
            <li className="chart-tooltip">
              <span>Measurement</span>{' '}
              <span>{row.measurementType}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}

